// M12.1 - GenericProgressionView (vista UNIFICADA estilo vrbuzzwire)
//
// Vista de progresion (N sesiones) para CUALQUIER juego de motricidad.
// Diseno alineado con la vista original de vrbuzzwire: dos columnas
// con la evolucion de la metrica principal del juego y la duracion.
//
//   - SIEMPRE: 2 columnas con line charts.
//   - Columna izquierda: evolucion de la metrica principal del juego
//     (resuelta como `summary.totalFaults` o `game.primaryMetric`).
//   - Columna derecha: evolucion de `durationSeconds`.
//   - Necesita al menos 2 sesiones para pintar lineas; con 1 muestra
//     Empty con mensaje claro.
//
// El detalle del payload esperado por sesion esta en
// `docs/revi-motricity/DASHBOARD_DATA_GUIDE.md`.

import React, { useMemo } from "react";
import { Alert, Card, Col, Empty, Row } from "antd";
import { useTranslation } from "react-i18next";
import { MotricityProgressionLine } from "../_shared";
import {
  modoDeProgresion,
  puntosDeProgresion,
} from "../_shared/serieDeProgresion";

function humanizeKey(key) {
  return String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

// La resolucion de que metrica pintar se movio a `serieDeProgresion.js`, que
// ademas decide si se puede pintar una linea o hay que decir que no.

export function GenericProgressionView({ sessions, game }) {
  const { t } = useTranslation("motricity");
  const ordered = useMemo(() => {
    const list = Array.isArray(sessions) ? sessions.slice() : [];
    return list.sort((a, b) => {
      const ad = new Date(a.startedAt || a.createdAt || 0).getTime();
      const bd = new Date(b.startedAt || b.createdAt || 0).getTime();
      return ad - bd;
    });
  }, [sessions]);

  // Que se puede pintar con estas sesiones. Ver serieDeProgresion.js: tiene
  // TRES salidas, y la tercera —"mixta"— es la que evita el error grave.
  const modo = useMemo(() => modoDeProgresion(ordered, game), [ordered, game]);
  const primaryKey = modo.claveLegacy;

  // Dos de las tres ramas son interfaz y estaban en castellano clavado; la
  // tercera deriva el nombre del campo que manda el juego, que es DATO.
  const primaryLabel = useMemo(() => {
    if (modo.modo === "core") return t("progressionView.puntuacion");
    if (!primaryKey) return t("progressionView.metricaPrincipal");
    if (primaryKey === "totalFaults")
      return t("progressionView.erroresTotales");
    return humanizeKey(primaryKey);
  }, [modo.modo, primaryKey, t]);

  const primarySeries = useMemo(() => {
    const puntos = puntosDeProgresion(ordered, modo, shortDate);
    if (puntos.length === 0) return [];
    return [
      {
        name:
          modo.modo === "core"
            ? t("progressionView.puntuacion")
            : primaryKey === "totalFaults"
              ? t("progressionView.series.totalFaults")
              : humanizeKey(primaryKey),
        points: puntos,
      },
    ];
  }, [ordered, modo, primaryKey, t]);

  const durationSeries = useMemo(
    () => [
      {
        name: t("progressionView.series.duration"),
        points: ordered.map((s) => ({
          date: shortDate(s.startedAt || s.createdAt),
          value: Number(s.durationSeconds || 0),
        })),
      },
    ],
    [ordered, t],
  );

  if (ordered.length === 0) {
    return (
      <Card variant="outlined">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("progressionView.empty")}
        />
      </Card>
    );
  }

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={12}>
        {modo.modo === "mixta" ? (
          // NO se pinta la linea, y es lo correcto. Ver serieDeProgresion.js:
          // media serie significaria "errores" y la otra media "puntuacion", y
          // el terapeuta leeria una evolucion que no existe.
          <Card title={t("progressionView.faultsTitle")} variant="outlined">
            <Alert
              type="info"
              showIcon
              message={t("progressionView.mixtaTitulo")}
              description={t("progressionView.mixtaDetalle", {
                nuevas: modo.conNucleo,
                total: modo.total,
              })}
            />
          </Card>
        ) : primarySeries.length > 0 ? (
          <MotricityProgressionLine
            title={t("progressionView.evolucionDe", {
              metrica: primaryLabel,
            })}
            series={primarySeries}
          />
        ) : (
          <Card title={t("progressionView.faultsTitle")} variant="outlined">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("progressionView.faultsEmpty")}
            />
          </Card>
        )}
      </Col>
      <Col xs={24} lg={12}>
        <MotricityProgressionLine
          title={t("progressionView.durationTitle")}
          series={durationSeries}
        />
      </Col>
    </Row>
  );
}
